import React, { useEffect, useState } from 'react';
import { Download, Share, PlusSquare, MoreVertical } from 'lucide-react';
import { canPromptInstall, isIOS, isStandalone, promptInstall, subscribeInstallState } from '../pwaInstall';

interface Props {
  variant?: 'cta' | 'icon';
  tone?: 'onDark' | 'onLight';
}

export default function InstallAppButton({ variant = 'cta', tone = 'onDark' }: Props) {
  const [canPrompt, setCanPrompt] = useState(canPromptInstall());
  const [standalone, setStandalone] = useState(isStandalone());
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeInstallState(() => {
      setCanPrompt(canPromptInstall());
      setStandalone(isStandalone());
    });
    return unsubscribe;
  }, []);

  if (standalone) return null;

  const handleClick = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === 'unavailable') setShowGuide(true);
    } else {
      setShowGuide(true);
    }
  };

  const label = 'Download App';

  return (
    <>
      {variant === 'cta' ? (
        <button
          onClick={handleClick}
          style={tone === 'onDark'
            ? { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '14px 28px', borderRadius: 12, fontSize: 16, fontWeight: 600, border: '1.5px solid rgba(255,255,255,0.35)', backdropFilter: 'blur(10px)', cursor: 'pointer', fontFamily: 'inherit' }
            : { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#1a56db,#0e9f6e)', color: '#fff', padding: '14px 28px', borderRadius: 12, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          <Download size={18} />{label}
        </button>
      ) : (
        <button
          onClick={handleClick}
          title={label}
          style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <Download size={16} color="var(--text-secondary)" />
        </button>
      )}

      {showGuide && <InstallGuideModal onClose={() => setShowGuide(false)} />}
    </>
  );
}

function InstallGuideModal({ onClose }: { onClose: () => void }) {
  const ios = isIOS();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontWeight: 600 }}>Install CTIMS</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div className="modal-body">
          {ios ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>On iPhone or iPad, add CTIMS to your Home Screen from Safari:</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}><Share size={18} style={{ flexShrink: 0 }} /> 1. Tap the <strong>Share</strong> button in Safari's toolbar</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}><PlusSquare size={18} style={{ flexShrink: 0 }} /> 2. Tap <strong>Add to Home Screen</strong></div>
              <div style={{ fontSize: 13, paddingLeft: 28 }}>3. Tap <strong>Add</strong> — CTIMS now opens like an app, full-screen</div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Must be opened in Safari (not Chrome/Instagram browser) for this option to appear.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>Your browser doesn't support one-tap install here, but you can still add CTIMS as an app:</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}><MoreVertical size={18} style={{ flexShrink: 0 }} /> Chrome/Edge (Android or desktop): open the <strong>⋮</strong> menu → <strong>Install app</strong> (or <strong>Add to Home screen</strong>)</div>
              <div style={{ fontSize: 13 }}>Firefox: menu → <strong>Install</strong>, if offered — otherwise bookmark the page for quick access</div>
              <div style={{ fontSize: 13 }}>Safari on Mac: <strong>File → Add to Dock</strong></div>
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-primary" onClick={onClose}>Got it</button></div>
      </div>
    </div>
  );
}
